// One page for a new invoice and for an existing one: the form, and (edit) the lifecycle actions.
import { useEffect, useRef, useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api } from '../../services/api';
import { errorMessage, useAsync } from '../../hooks/useAsync';
import { dateTime, lineTotal, money, toDecimal } from '../../services/format';
import { OrgPicker } from '../../components/OrgPicker';
import { QontoInvoice } from './QontoInvoice';
import { Alert, Badge, Button, Card, Confirm, Field, Input, KeyValue, PageTitle, Textarea, statusTone } from '../../components/ui';
import type { InvoiceAdmin, InvoiceCreate, InvoiceLine, OrganizationAdmin } from '../../types/api';

interface Line {
  description: string;
  quantity: string;
  unit_price: string;
}

const EMPTY_LINE: Line = { description: '', quantity: '1', unit_price: '' };

export function InvoiceForm({ mode }: { mode: 'new' | 'edit' }) {
  const { id } = useParams();
  const invoiceId = mode === 'edit' ? Number(id) : null;
  const [params] = useSearchParams();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { data: invoice, error, reload } = useAsync(() => (invoiceId === null ? Promise.resolve(null) : api.invoice(invoiceId)), [invoiceId]);
  const presetOrgId = params.get('organization_id');
  const [org, setOrg] = useState<OrganizationAdmin | null>(null);
  const [form, setForm] = useState({ amount: '', currency: 'EUR', description: '', period_start: '', period_end: '', issued_at: '', due_at: '', external_ref: '', notes: '' });
  const [lines, setLines] = useState<Line[]>([]);
  const [busy, setBusy] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [fail, setFail] = useState<string | null>(null);
  const [confirm, setConfirm] = useState<'send' | 'void' | 'delete' | 'paid' | null>(null);
  const [paidAt, setPaidAt] = useState('');
  const [sentTo, setSentTo] = useState<string | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (invoice) {
      setForm({ amount: invoice.amount, currency: invoice.currency, description: invoice.description, period_start: invoice.period_start ?? '', period_end: invoice.period_end ?? '', issued_at: invoice.issued_at ?? '', due_at: invoice.due_at ?? '', external_ref: invoice.external_ref ?? '', notes: invoice.notes });
      setLines(invoice.lines.map((l) => ({ description: l.description, quantity: String(l.quantity), unit_price: String(l.unit_price) })));
    }
  }, [invoice]);
  useEffect(() => {
    if (mode === 'new' && presetOrgId) {
      api.organizations({ q: undefined, page_size: 1 }).catch(() => null);
      api.organization(Number(presetOrgId)).then((o) => setOrg(o), () => undefined);
    }
  }, [mode, presetOrgId]);

  const locked = invoice !== null && invoice.status !== 'draft';
  const total = lines.reduce((sum, l) => sum + lineTotal(l.quantity, l.unit_price), 0);
  const field = (key: keyof typeof form) => ({ value: form[key], onChange: (e: { target: { value: string } }) => setForm((f) => ({ ...f, [key]: e.target.value })) });

  const payload = (): Omit<InvoiceCreate, 'organization_id'> => ({
    amount: toDecimal(form.amount) ?? '0.00',
    currency: form.currency.trim().toUpperCase() || 'EUR',
    description: form.description,
    lines: lines.filter((l) => l.description.trim()).map<InvoiceLine>((l) => ({ description: l.description.trim(), quantity: l.quantity || '1', unit_price: toDecimal(l.unit_price) ?? '0.00' })),
    period_start: form.period_start || null,
    period_end: form.period_end || null,
    issued_at: form.issued_at || null,
    due_at: form.due_at || null,
    external_ref: form.external_ref.trim() || null,
    notes: form.notes,
  });

  const run = async (name: string, fn: () => Promise<unknown>, done?: string) => {
    setBusy(name);
    setFail(null);
    setNotice(null);
    try {
      await fn();
      if (done) setNotice(done);
      reload();
    } catch (err) {
      setFail(errorMessage(err));
    } finally {
      setBusy(null);
    }
  };

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (mode === 'new') {
      if (!org) return;
      setBusy('save');
      setFail(null);
      try {
        const created = await api.createInvoice({ organization_id: org.id, ...payload() });
        navigate(`/invoices/${created.id}`, { replace: true });
      } catch (err) {
        setFail(errorMessage(err));
      } finally {
        setBusy(null);
      }
      return;
    }
    if (invoice) await run('save', () => api.patchInvoice(invoice.id, payload()), t('common.saved'));
  };

  const send = async (inv: InvoiceAdmin) => {
    await run('send', () => api.sendInvoice(inv.id), t('invoices.sent'));
    const events = await api.audit({ action: 'invoice.sent', target_kind: 'invoice', target_id: String(inv.id), page_size: 1 }).catch(() => null);
    const to = events?.items[0]?.data.to;
    setSentTo(typeof to === 'string' ? to : null);
  };

  const download = async (inv: InvoiceAdmin) => {
    const blob = await api.downloadInvoicePdf(inv.id);
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = inv.pdf_name ?? `${inv.number}.pdf`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (error) return <Alert>{error}</Alert>;
  if (mode === 'edit' && !invoice) return <p className="text-faint">{t('common.loading')}</p>;
  // An invoice issued through Qonto is read-only: Qonto numbers it and only a credit note changes it (M2).
  if (invoice && invoice.provider === 'qonto') return <QontoInvoice invoice={invoice} reload={reload} />;

  return (
    <div>
      <PageTitle
        title={
          invoice ? (
            <>
              {invoice.number} <Badge tone={statusTone(invoice.status)}>{t(`invoices.status.${invoice.status}`)}</Badge>
            </>
          ) : (
            t('invoices.new')
          )
        }
        subtitle={invoice ? <Link to={`/organizations/${invoice.organization_id}?tab=invoices`} className="text-accent underline">{invoice.organization_name}</Link> : t('invoices.newSubtitle')}
        actions={
          invoice && (
            <>
              <input ref={fileInput} type="file" accept="application/pdf" className="hidden" data-testid="pdf-input" onChange={(e) => { const f = e.target.files?.[0]; if (f) void run('pdf', () => api.uploadInvoicePdf(invoice.id, f), t('invoices.pdfUploaded')); e.target.value = ''; }} />
              <Button onClick={() => fileInput.current?.click()} busy={busy === 'pdf'}>
                {invoice.has_pdf ? t('invoices.replacePdf') : t('invoices.uploadPdf')}
              </Button>
              {invoice.has_pdf && <Button onClick={() => void download(invoice)}>{t('invoices.downloadPdf')}</Button>}
              {(invoice.status === 'draft' || invoice.status === 'sent') && (
                <Button variant="primary" onClick={() => setConfirm('send')} data-testid="invoice-send">
                  {invoice.status === 'sent' ? t('invoices.resend') : t('invoices.send')}
                </Button>
              )}
              {(invoice.status === 'draft' || invoice.status === 'sent') && <Button onClick={() => setConfirm('paid')}>{t('invoices.markPaid')}</Button>}
              {invoice.status !== 'paid' && invoice.status !== 'void' && (
                <Button variant="danger" onClick={() => setConfirm('void')}>
                  {t('invoices.void')}
                </Button>
              )}
              {invoice.status === 'draft' && (
                <Button variant="danger" onClick={() => setConfirm('delete')}>
                  {t('common.delete')}
                </Button>
              )}
            </>
          )
        }
      />
      {notice && (
        <div className="mb-3">
          <Alert tone="good">
            {notice}
            {sentTo && ` — ${t('invoices.sentTo', { to: sentTo })}`}
          </Alert>
        </div>
      )}
      {fail && <div className="mb-3"><Alert>{fail}</Alert></div>}
      {locked && <div className="mb-3"><Alert tone="warn">{t('invoices.locked')}</Alert></div>}
      <form onSubmit={submit} className="grid gap-4 lg:grid-cols-[2fr_1fr]">
        <div className="flex flex-col gap-4">
          <Card title={t('invoices.details')} actions={<Button type="submit" variant="primary" busy={busy === 'save'} disabled={mode === 'new' ? !org : locked} data-testid="invoice-save">{mode === 'new' ? t('common.create') : t('common.save')}</Button>}>
            <div className="flex flex-col gap-3">
              {mode === 'new' && (
                <Field label={t('invoices.organization')}>
                  <OrgPicker value={org} onChange={setOrg} />
                </Field>
              )}
              <div className="grid grid-cols-3 gap-3">
                <Field label={t('invoices.amount')} className="col-span-2">
                  <Input type="number" min={0} step="0.01" required disabled={locked} {...field('amount')} data-testid="invoice-amount" />
                </Field>
                <Field label={t('invoices.currency')}>
                  <Input maxLength={3} disabled={locked} {...field('currency')} />
                </Field>
              </div>
              <Field label={t('invoices.description')}>
                <Input disabled={locked} {...field('description')} />
              </Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label={t('invoices.periodStart')}>
                  <Input type="date" disabled={locked} {...field('period_start')} />
                </Field>
                <Field label={t('invoices.periodEnd')}>
                  <Input type="date" disabled={locked} {...field('period_end')} />
                </Field>
                <Field label={t('invoices.issuedAt')}>
                  <Input type="date" disabled={locked} {...field('issued_at')} />
                </Field>
                <Field label={t('invoices.dueAt')}>
                  <Input type="date" disabled={locked} {...field('due_at')} />
                </Field>
              </div>
              <Field label={t('invoices.externalRef')} hint={t('invoices.externalRefHint')}>
                <Input disabled={locked} {...field('external_ref')} />
              </Field>
              <Field label={t('invoices.notes')} hint={t('orgs.notesHint')}>
                <Textarea disabled={locked} {...field('notes')} />
              </Field>
            </div>
          </Card>
          <Card
            title={t('invoices.lines')}
            actions={
              !locked && (
                <Button onClick={() => setLines((ls) => [...ls, { ...EMPTY_LINE }])}>
                  {t('invoices.addLine')}
                </Button>
              )
            }
          >
            {lines.length === 0 ? (
              <p className="text-faint">{t('invoices.noLines')}</p>
            ) : (
              <div className="flex flex-col gap-2">
                {lines.map((line, i) => (
                  <div key={i} className="grid grid-cols-[1fr_80px_120px_110px_32px] items-center gap-2">
                    <Input placeholder={t('invoices.lineDescription')} disabled={locked} value={line.description} onChange={(e) => setLines((ls) => ls.map((l, j) => (j === i ? { ...l, description: e.target.value } : l)))} />
                    <Input type="number" step="any" placeholder={t('invoices.quantity')} disabled={locked} value={line.quantity} onChange={(e) => setLines((ls) => ls.map((l, j) => (j === i ? { ...l, quantity: e.target.value } : l)))} />
                    <Input type="number" step="0.01" placeholder={t('invoices.unitPrice')} disabled={locked} value={line.unit_price} onChange={(e) => setLines((ls) => ls.map((l, j) => (j === i ? { ...l, unit_price: e.target.value } : l)))} />
                    <span className="text-right font-mono text-[11.5px] text-muted">{money(lineTotal(line.quantity, line.unit_price), form.currency || 'EUR', i18n.language)}</span>
                    <Button variant="ghost" disabled={locked} onClick={() => setLines((ls) => ls.filter((_, j) => j !== i))} aria-label={t('common.delete')}>
                      ×
                    </Button>
                  </div>
                ))}
                <div className="flex items-center justify-end gap-3 border-t border-line-soft pt-2">
                  <span className="font-mono text-[11px] text-faint">{t('invoices.linesTotal')}</span>
                  <span className="font-mono text-[12.5px] text-ink" data-testid="lines-total">
                    {money(total, form.currency || 'EUR', i18n.language)}
                  </span>
                  {!locked && (
                    <Button onClick={() => setForm((f) => ({ ...f, amount: total.toFixed(2) }))}>{t('invoices.useTotal')}</Button>
                  )}
                </div>
              </div>
            )}
          </Card>
        </div>
        {invoice && (
          <Card title={t('invoices.lifecycle')}>
            <KeyValue rows={[[t('invoices.statusLabel'), t(`invoices.status.${invoice.status}`)], [t('invoices.pdf'), invoice.has_pdf ? invoice.pdf_name : t('common.no')], [t('invoices.sentAt'), dateTime(invoice.sent_at, i18n.language)], [t('invoices.paidAt'), dateTime(invoice.paid_at, i18n.language)], [t('orgs.createdAt'), dateTime(invoice.created_at, i18n.language)], [t('orgs.updatedAt'), dateTime(invoice.updated_at, i18n.language)]]} />
          </Card>
        )}
      </form>
      {invoice && (
        <>
          <Confirm open={confirm === 'send'} title={t('invoices.sendTitle')} message={invoice.has_pdf ? t('invoices.sendMessage') : t('invoices.sendNoPdf')} confirmLabel={t('invoices.send')} busy={busy === 'send'} onCancel={() => setConfirm(null)} onConfirm={() => void send(invoice).then(() => setConfirm(null))} />
          <Confirm open={confirm === 'void'} danger title={t('invoices.voidTitle')} message={t('invoices.voidMessage')} confirmLabel={t('invoices.void')} busy={busy === 'void'} onCancel={() => setConfirm(null)} onConfirm={() => void run('void', () => api.voidInvoice(invoice.id), t('invoices.voided')).then(() => setConfirm(null))} />
          <Confirm open={confirm === 'delete'} danger title={t('invoices.deleteTitle')} message={t('invoices.deleteMessage')} confirmLabel={t('common.delete')} busy={busy === 'delete'} onCancel={() => setConfirm(null)} onConfirm={() => void run('delete', () => api.deleteInvoice(invoice.id)).then(() => navigate('/invoices', { replace: true }))} />
          <Confirm
            open={confirm === 'paid'}
            title={t('invoices.paidTitle')}
            message={
              <Field label={t('invoices.paidAt')} hint={t('invoices.paidAtHint')}>
                <Input type="date" value={paidAt} onChange={(e) => setPaidAt(e.target.value)} />
              </Field>
            }
            confirmLabel={t('invoices.markPaid')}
            busy={busy === 'paid'}
            onCancel={() => setConfirm(null)}
            onConfirm={() => void run('paid', () => api.markInvoicePaid(invoice.id, paidAt ? `${paidAt}T12:00:00Z` : null), t('invoices.paid')).then(() => setConfirm(null))}
          />
        </>
      )}
    </div>
  );
}
